import { HEADER_TOOLBAR_MIN_HEIGHT } from './layoutSpacing';
import { LEARNING_LAB_BANNER_HEIGHT_CSS_VAR } from './learningLabLayout';

/** iOS standalone / viewport-fit=cover insets. */
export const PWA_SAFE_AREA_TOP = 'env(safe-area-inset-top, 0px)';
export const PWA_SAFE_AREA_BOTTOM = 'env(safe-area-inset-bottom, 0px)';
export const PWA_SAFE_AREA_LEFT = 'env(safe-area-inset-left, 0px)';
export const PWA_SAFE_AREA_RIGHT = 'env(safe-area-inset-right, 0px)';

/** Sticky PWA AppBar offset for drawers and full-height panels (toolbar + top inset). */
export const PWA_STICKY_HEADER_OFFSET = `calc(${HEADER_TOOLBAR_MIN_HEIGHT.xs}px + ${PWA_SAFE_AREA_TOP})`;

/** Full viewport height minus top and bottom safe areas. */
export const PWA_SAFE_AREA_VIEWPORT_HEIGHT = `calc(100% - ${PWA_SAFE_AREA_TOP} - ${PWA_SAFE_AREA_BOTTOM})`;

/** Toolbar sx without safe-area inset (Learning Lab banner already clears the notch). */
export const pwaAppBarToolbarBaseSx = {
  minHeight: {
    xs: `${HEADER_TOOLBAR_MIN_HEIGHT.xs}px !important`,
    sm: `${HEADER_TOOLBAR_MIN_HEIGHT.sm}px !important`,
  },
} as const;

/**
 * AppBar shell sx: paint brand/gradient under the status bar, keep controls below it.
 * Safe-area padding belongs on the shell — not on the inner Toolbar (Copilot/iOS pattern).
 */
export function resolvePwaAppBarShellSx(learningLabBannerActive: boolean) {
  return learningLabBannerActive ? {} : { pt: PWA_SAFE_AREA_TOP };
}

export function resolvePwaAppBarToolbarSx(_learningLabBannerActive: boolean) {
  return pwaAppBarToolbarBaseSx;
}

type ComposePwaAppBarToolbarSxOptions = {
  learningLabBannerActive: boolean;
  subtitle?: boolean;
  /** Site chrome uses responsive minHeight when a subtitle is shown. */
  siteChrome?: boolean;
};

/**
 * Toolbar sx for PWA AppBars. Never use `py` here — it would wipe shell `pt`.
 * Safe-area inset is applied on the AppBar via resolvePwaAppBarShellSx().
 */
export function composePwaAppBarToolbarSx({
  learningLabBannerActive,
  subtitle = false,
  siteChrome = false,
}: ComposePwaAppBarToolbarSxOptions) {
  const base = resolvePwaAppBarToolbarSx(learningLabBannerActive);

  if (!subtitle) {
    return { ...base, pb: 0 };
  }

  if (siteChrome) {
    return {
      ...base,
      minHeight: { xs: '64px !important', sm: '66px !important' },
      pb: 0.5,
    };
  }

  return {
    ...base,
    minHeight: '64px !important',
    pb: 0.5,
  };
}

/** Sticky mobile chrome shell outside PWAWrapper (e.g. in-shell event pages). */
export const pwaStickyChromeShellSx = {
  position: 'sticky' as const,
  top: 0,
  pt: PWA_SAFE_AREA_TOP,
} as const;

/** Mobile bottom nav spacer — matches fixed BottomNavigation + home indicator inset. */
export const PWA_MOBILE_BOTTOM_CHROME_SPACER = `calc(${HEADER_TOOLBAR_MIN_HEIGHT.xs}px + ${PWA_SAFE_AREA_BOTTOM})`;

/** Sticky shell for pages without PWAWrapper; defers top inset when Learning Lab is active. */
export function resolvePwaStickyChromeShellSx(learningLabBannerActive: boolean) {
  if (learningLabBannerActive) {
    return {
      position: 'sticky' as const,
      top: `var(${LEARNING_LAB_BANNER_HEIGHT_CSS_VAR}, 72px)`,
    };
  }

  return pwaStickyChromeShellSx;
}

/** Fixed bottom navigation sx: keep tabs above the home indicator. */
export const pwaBottomNavigationSafeAreaSx = {
  pb: PWA_SAFE_AREA_BOTTOM,
  pl: PWA_SAFE_AREA_LEFT,
  pr: PWA_SAFE_AREA_RIGHT,
} as const;

/**
 * Temporary (mobile) drawer paper: offset below status bar and above home indicator.
 * Matches fixed drawer pattern — do not rely on padding alone.
 */
export const pwaTemporaryDrawerPaperSx = {
  top: `${PWA_SAFE_AREA_TOP} !important`,
  left: `${PWA_SAFE_AREA_LEFT} !important`,
  height: `${PWA_SAFE_AREA_VIEWPORT_HEIGHT} !important`,
  '@supports (height: 100dvh)': {
    height: `calc(100dvh - ${PWA_SAFE_AREA_TOP} - ${PWA_SAFE_AREA_BOTTOM}) !important`,
  },
  paddingRight: PWA_SAFE_AREA_RIGHT,
  paddingBottom: PWA_SAFE_AREA_BOTTOM,
  boxSizing: 'border-box',
} as const;

/** Desktop drawer paper offset under the sticky PWA header. */
export const pwaPersistentDrawerPaperSx = {
  top: PWA_STICKY_HEADER_OFFSET,
  height: `calc(100vh - ${HEADER_TOOLBAR_MIN_HEIGHT.xs}px - ${PWA_SAFE_AREA_TOP})`,
  '@supports (height: 100dvh)': {
    height: `calc(100dvh - ${HEADER_TOOLBAR_MIN_HEIGHT.xs}px - ${PWA_SAFE_AREA_TOP})`,
  },
} as const;

/** @deprecated Use AppBar shell padding; drawer paper is top-offset instead. */
export const pwaAppBarToolbarSafeAreaSx = pwaAppBarToolbarBaseSx;

/** @deprecated Drawer paper top offset replaces header negative margin. */
export const pwaTemporaryDrawerHeaderSx = {
  p: 2,
} as const;
